import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import BookingPage from "./pages/BookingPage";
import VehiclesPage from "./pages/VehiclesPage";
import CarpoolPage from "./pages/CarpoolPage";
import LiveTrackingPage from "./pages/LiveTrackingPage";
import DriverTrackingPage from "./pages/DriverTrackingPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminSlotsPage from "./pages/AdminSlotsPage";
import AdminTimeSlotsPage from "./pages/AdminTimeSlotsPage";
import MasterAdminDashboard from "./pages/master_admin/AdminDashboard";
import AllClients from "./pages/master_admin/AllClients";
import ClientDetails from "./pages/master_admin/clients/ClientDetails";
import { PaymentsPage } from "./pages/master_admin/paymentsPage";
import PaymentsDetails from "./pages/master_admin/payments/paymentsDetails";
import TodaysBookingStatus from "./pages/TodaysBookingStatus";
import AttendantPage from "./pages/AttendantPage";
import UserProfile from "./pages/userProfile";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth with refresh token support
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        originalRequest._retry = true;
        try {
          const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
          const newToken = res.data.access_token;
          localStorage.setItem("token", newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshErr) {
          localStorage.removeItem("token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("user");
          window.location.href = "/";
        }
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);

const ProtectedRoute = ({ children, adminOnly = false, roles = null }) => {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  if (!token) {
    return <Navigate to="/" />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  if (adminOnly && user.role !== "admin" && user.role !== "master") {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/booking"
            element={
              <ProtectedRoute>
                <BookingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles"
            element={
              <ProtectedRoute>
                <VehiclesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/carpool"
            element={
              <ProtectedRoute>
                <CarpoolPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/carpool/track/:requestId"
            element={
              <ProtectedRoute>
                <LiveTrackingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/carpool/drive/:requestId"
            element={
              <ProtectedRoute>
                <DriverTrackingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-profile"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendant"
            element={
              <ProtectedRoute roles={["attendant", "admin", "master"]}>
                <AttendantPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bookings-today"
            element={
              <ProtectedRoute adminOnly>
                <TodaysBookingStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute adminOnly>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/slots"
            element={
              <ProtectedRoute adminOnly>
                <AdminSlotsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/timeslots"
            element={
              <ProtectedRoute adminOnly>
                <AdminTimeSlotsPage />
              </ProtectedRoute>
            }
          />
           <Route
            path="/master/dashboard"
            element={
              <ProtectedRoute adminOnly>
                <MasterAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/master/AllClients"
            element={
              <ProtectedRoute adminOnly>
                <AllClients />
              </ProtectedRoute>
            }
          />
           <Route
            path="/master/clientDetails/:clientId"
            element={
              <ProtectedRoute adminOnly>
                <ClientDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/master/payments"
            element={
              <ProtectedRoute adminOnly>
                <PaymentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/master/paymentsDetails/:paymentId"
            element={
              <ProtectedRoute adminOnly>
                <PaymentsDetails />
              </ProtectedRoute>
            }
          />
        </Routes>
        
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
export { API };